import React, { useEffect, useState } from "react";
import {
  Settings,
  ShieldCheck,
  ShoppingCart,
  RotateCcw,
  Wallet,
  Save,
  Clock3,
  Hash,
  CheckCircle,
  AlertCircle,
  X,
} from "lucide-react";
import {
  useClearCacheMutation,
  useGetAdminSettingsQuery,
  useUpdateAdminSettingMutation,
  useUpdateAdminSettingsMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";
import {
  IAdminSettingRequest,
  IApplicationSettingGroup,
  IApplicationSettingsResponse,
  IUpdateApplicationSettingsRequest,
} from "../../../types/Request/AdminRequest";
import DynamicSettingInput from "../../../components/Admin/DynamicSettingInput";
import CacheActionCard from "../../../components/Admin/CacheActionCard";
import {
  CLEAR_ALL_CACHE,
  CLEAR_CART_CACHE,
  CLEAR_DASHBOARD_CACHE,
  CLEAR_HOME_CACHE,
  CLEAR_SEARCH_CACHE,
} from "../../../config/apiUrlsAdmin";
import { useAlert } from "../../../components/customer/AlertContext";

interface AdminSettingItem {
  key: string;
  displayName: string;
  description: string;
  value: string;
  group: string;
  dataType: "Integer" | "Boolean" | "String";
  isEditable: boolean;
  isDisplay: boolean;
}

interface Toast {
  id: string;
  type: "success" | "error";
  title: string;
  message: string;
}

const AdminSettings: React.FC = () => {

  const { showConfirm } = useAlert();

  const [toasts, setToasts] = useState<Toast[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [savingFields, setSavingFields] = useState<Set<string>>(new Set());

  //--------------------------------------------------
  // Load Application Settings
  //--------------------------------------------------

  const {
    data: adminSettings,
    isLoading,
    refetch,
  } = useGetAdminSettingsQuery();
  const [updateAdminSettings, { isLoading: isSaving }] =
    useUpdateAdminSettingsMutation();

  //--------------------------------------------------
  // Cache Management
  //--------------------------------------------------

  const [clearCache, { isLoading: isClearingCache }] = useClearCacheMutation();

  //--------------------------------------------------
  // Application Setting Groups
  //--------------------------------------------------

  const groups = adminSettings?.groups ?? [];

  const [selectedGroup, setSelectedGroup] = useState("");
  //--------------------------------------------------
  // Editable Settings
  //--------------------------------------------------

  const [editedSettings, setEditedSettings] = useState<Record<string, string>>(
    {}
  );

  //--------------------------------------------------
  // Original Settings
  // Used to detect modified values.
  //--------------------------------------------------

  const [originalSettings, setOriginalSettings] = useState<
    Record<string, string>
  >({});

  //--------------------------------------------------
  // Initialize editable values
  //--------------------------------------------------

  useEffect(() => {
    if (!adminSettings) return;

    const values: Record<string, string> = {};

    adminSettings.groups.forEach((group) => {
      group.settings.forEach((setting) => {
        values[setting.key] = setting.value;
      });
    });

    setEditedSettings(values);

    setOriginalSettings(values);
  }, [adminSettings]);

  //--------------------------------------------------
  // Unsaved Changes
  //--------------------------------------------------

  const modifiedSettings = Object.keys(editedSettings).filter(
    (key) => editedSettings[key] !== originalSettings[key]
  );

  const hasChanges = modifiedSettings.length > 0;

  //--------------------------------------------------
  // Update setting value
  //--------------------------------------------------

  const updateSettingValue = (key: string, value: string) => {
    setEditedSettings((previous) => ({
      ...previous,

      [key]: value,
    }));
  };

  const [updateAdminSetting] = useUpdateAdminSettingMutation();

  useEffect(() => {
    if (groups.length > 0 && !selectedGroup) {
      setSelectedGroup(groups[0].group);
    }
  }, [groups]);

  //--------------------------------------------------
  // Active Settings Group
  //--------------------------------------------------

  const currentGroup = groups.find(
    (x: IApplicationSettingGroup) => x.group === selectedGroup
  );

  // Add toast notification
  const addToast = (
    type: "success" | "error",
    title: string,
    message: string
  ) => {
    const id = Date.now().toString();
    const newToast: Toast = { id, type, title, message };
    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  // Remove toast notification
  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  //--------------------------------------------------
  // Initialize selected group
  //--------------------------------------------------

  useEffect(() => {
    if (adminSettings && adminSettings.groups.length > 0 && !selectedGroup) {
      setSelectedGroup(adminSettings.groups[0].group);
    }
  }, [adminSettings, selectedGroup]);

  // Handle field value change
  const handleFieldChange = (key: string, value: string) => {
    setFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  //--------------------------------------------------
  // Reset Changes
  //--------------------------------------------------

  const resetChanges = () => {
    setEditedSettings(originalSettings);
  };

  //--------------------------------------------------
  // Build Update Request
  //--------------------------------------------------

  const buildUpdateRequest: IUpdateApplicationSettingsRequest = {
    settings: modifiedSettings.map((key) => ({
      key,

      value: editedSettings[key],
    })),
  };

  //--------------------------------------------------
  // Save Changes
  //--------------------------------------------------

  const saveChanges = async () => {
    if (!hasChanges) return;

    try {
      await updateAdminSettings(buildUpdateRequest).unwrap();

      //--------------------------------------------------
      // Refresh latest settings
      //--------------------------------------------------

      await refetch();

      addToast(
        "success",
        "Success",
        "Application settings updated successfully."
      );
    } catch {
      addToast("error", "Error", "Failed to update application settings.");
    }
  };

  //--------------------------------------------------
  // Clear Cache
  //--------------------------------------------------

  const clearApplicationCache = async (url: string, successMessage: string) => {
    try {
      await clearCache(url).unwrap();

      addToast("success", "Success", successMessage);
    } catch {
      addToast("error", "Error", "Unable to clear cache.");
    }
  };

  const clearAllCaches = () => {
    showConfirm({
      title: "Clear All Cache",
      message: "Are you sure you want to clear all application caches?",
      type: "warning",
      confirmText: "Clear All",
      cancelText: "Dismiss",
      showCancel: true,
      onConfirm: () => {
        clearApplicationCache(
          CLEAR_ALL_CACHE,
          "All application caches cleared successfully."
        );
      },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            toast={toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      <div className="w-full animate-in fade-in duration-500">
        {/* Main Settings Container */}
        <div className="w-full bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Settings Header */}
          <div className="border-b border-slate-200 p-4 md:p-5">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-black text-slate-900">
                Application Settings
              </h2>
            </div>
            <p className="text-xs text-slate-400 font-medium mt-1">
              Configure authentication, orders, refunds, and wallet settings.
            </p>
          </div>

          {/*--------------------------------------------------
    Settings Content
---------------------------------------------------*/}
          <div className="p-4 md:p-5">
            {/*--------------------------------------------------
      Settings Group Tabs
  ---------------------------------------------------*/}
            <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-4 mb-6">
              {groups.map((group: IApplicationSettingGroup) => (
                <button
                  key={group.group}
                  type="button"
                  onClick={() => setSelectedGroup(group.group)}
                  className={`
        flex
        items-center
        gap-2
        px-5
        py-2.5
        rounded-lg
        text-sm
        font-semibold
        transition-all
        duration-200
        border

        ${
          selectedGroup === group.group
            ? "bg-[#2F5D2F] text-white border-[#2F5D2F] shadow-sm"
            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
        }
    `}
                >
                  {group.displayName}
                </button>
              ))}
            </div>
            {/*--------------------------------------------------
    Save Changes
---------------------------------------------------*/}

            <div className="mb-6 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <p className="font-medium text-slate-700">
                  {hasChanges
                    ? `${modifiedSettings.length} unsaved change(s)`
                    : "No pending changes"}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={resetChanges}
                  disabled={!hasChanges || isSaving}
                  className="
        rounded-lg
        border
        border-slate-300
        px-4
        py-2
        text-sm
        transition
        disabled:opacity-50
        disabled:cursor-not-allowed
    "
                >
                  Reset
                </button>

                <button
                  type="button"
                  onClick={saveChanges}
                  disabled={!hasChanges || isSaving}
                  className="
        rounded-lg
        bg-[#2F5D2F]
        px-5
        py-2
        text-sm
        font-semibold
        text-white
        transition
        disabled:opacity-50
        disabled:cursor-not-allowed
    "
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
            {/*--------------------------------------------------
    Selected Group Header
---------------------------------------------------*/}

            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {currentGroup?.displayName}
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  Configure all settings related to {currentGroup?.displayName}.
                </p>
              </div>
            </div>

            {/*--------------------------------------------------
      Active Settings
  ---------------------------------------------------*/}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentGroup?.settings.map((setting) => (
                <div
                  key={setting.key}
                  className="
bg-white
rounded-xl
border
border-slate-200
shadow-sm
p-5
transition-all
hover:shadow-md
"
                >
                  <div className="mb-3">
                    <h3 className="font-semibold text-slate-900">
                      {setting.displayName}
                    </h3>

                    <p className="text-sm text-slate-500 mt-1">
                      {setting.description}
                    </p>
                  </div>

                  <DynamicSettingInput
                    setting={setting}
                    value={editedSettings[setting.key] ?? setting.value}
                    onChange={(value) => updateSettingValue(setting.key, value)}
                  />
                  {/*--------------------------------------------------
    Setting Metadata
---------------------------------------------------*/}

                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                    {setting.defaultValue && (
                      <div>
                        <span className="font-medium">Default:</span>{" "}
                        {setting.defaultValue}
                      </div>
                    )}

                    {setting.minValue && setting.maxValue && (
                      <div>
                        <span className="font-medium">Allowed:</span>{" "}
                        {setting.minValue} - {setting.maxValue}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/*--------------------------------------------------
    System Maintenance
---------------------------------------------------*/}

            <div className="mt-12">
              <h2 className="text-xl font-bold text-slate-900">
                System Maintenance
              </h2>

              <p className="mt-2 text-sm text-slate-500">
                Clear cached application data after major configuration or
                content updates.
              </p>

              {/* Cache cards will go here */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <CacheActionCard
                  title="Home Screen Cache"
                  description="Refresh homepage banners, sections and featured products."
                  onClick={() =>
                    clearApplicationCache(
                      CLEAR_HOME_CACHE,
                      "Home cache cleared successfully."
                    )
                  }
                />

                <CacheActionCard
                  title="Search Cache"
                  description="Clear cached search suggestions and results."
                  onClick={() =>
                    clearApplicationCache(
                      CLEAR_SEARCH_CACHE,
                      "Search cache cleared successfully."
                    )
                  }
                />

                <CacheActionCard
                  title="Dashboard Cache"
                  description="Refresh admin dashboard statistics."
                  onClick={() =>
                    clearApplicationCache(
                      CLEAR_DASHBOARD_CACHE,
                      "Dashboard cache cleared successfully."
                    )
                  }
                />

                <CacheActionCard
                  title="Cart Cache"
                  description="Clear cached customer cart information."
                  onClick={() =>
                    clearApplicationCache(
                      CLEAR_CART_CACHE,
                      "Cart cache cleared successfully."
                    )
                  }
                />
              </div>
              <div className="mt-8 border-t border-slate-200 pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-red-600">
                      Clear All Application Cache
                    </h3>

                    <p className="mt-1 text-sm text-slate-500">
                      Clears all cached application data. The application may
                      respond slightly slower while caches rebuild.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="
            rounded-lg
            bg-red-600
            px-5
            py-2
            text-sm
            font-semibold
            text-white
            hover:bg-red-700
        "
                    onClick={clearAllCaches}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SettingsSectionProps {
  icon: React.ReactNode;
  iconBackground: string;
  title: string;
  description: string;
  children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({
  icon,
  iconBackground,
  title,
  description,
  children,
}) => {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      {/* Section Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 bg-slate-50/60 border-b border-slate-100">
        <div
          className={`w-8 h-8 rounded-lg ${iconBackground} flex items-center justify-center shrink-0`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <h3 className="text-sm font-black text-slate-900">{title}</h3>

          <p className="text-[11px] text-slate-400 font-medium mt-0.5">
            {description}
          </p>
        </div>
      </div>

      {/* Section Content */}
      <div className="p-4">{children}</div>
    </div>
  );
};

interface SettingFieldProps {
  item: AdminSettingItem;
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  isSaving: boolean;
}

interface NumberInputProps {
  value: string;
  onChange: (value: string) => void;
  isInvalid: boolean;
  fieldKey: string;
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  isInvalid,
}) => {
  return (
    <div
      className={`flex items-center h-10 bg-slate-50 border rounded-lg overflow-hidden transition ${
        isInvalid
          ? "border-red-300 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-100"
          : "border-slate-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10"
      }`}
    >
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => {
          const value = e.target.value;

          // Allow empty value while editing
          if (value === "") {
            onChange(value);
            return;
          }

          // Prevent negative values
          if (Number(value) >= 0) {
            onChange(value);
          }
        }}
        className={`w-full h-full bg-transparent px-3 text-sm font-bold outline-none min-w-0 ${
          isInvalid ? "text-red-700" : "text-slate-700"
        }`}
      />
    </div>
  );
};

interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
}

const TextInput: React.FC<TextInputProps> = ({ value, onChange }) => {
  return (
    <div className="flex items-center h-10 bg-slate-50 border border-slate-200 rounded-lg overflow-hidden transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-full bg-transparent px-3 text-sm font-bold outline-none min-w-0 text-slate-700"
      />
    </div>
  );
};

interface BooleanInputProps {
  value: boolean;
  onChange: (checked: boolean) => void;
}

const BooleanInput: React.FC<BooleanInputProps> = ({ value, onChange }) => {
  return (
    <div className="h-10 flex items-center justify-between gap-3 px-3 bg-slate-50 border border-slate-200 rounded-lg">
      <span className="text-[11px] text-slate-400 font-medium truncate">
        {value ? "Enabled" : "Disabled"}
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`relative shrink-0 w-10 h-6 rounded-full border transition-all duration-300 ${
          value ? "bg-primary border-primary" : "bg-slate-300 border-slate-400"
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 ${
            value ? "left-[18px]" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
};

interface ToastProps {
  toast: Toast;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const isSuccess = toast.type === "success";
  const bgColor = isSuccess ? "bg-emerald-50" : "bg-red-50";
  const borderColor = isSuccess ? "border-emerald-200" : "border-red-200";
  const textColor = isSuccess ? "text-emerald-900" : "text-red-900";
  const titleColor = isSuccess ? "text-emerald-900" : "text-red-900";
  const iconColor = isSuccess ? "text-emerald-600" : "text-red-600";
  const Icon = isSuccess ? CheckCircle : AlertCircle;

  return (
    <div
      className={`
        animate-in
        slide-in-from-right-full
        duration-300
        flex
        items-start
        gap-3
        w-96
        px-4
        py-4
        rounded-lg
        border
        ${bgColor}
        ${borderColor}
        shadow-lg
        pointer-events-auto
      `}
    >
      {/* Icon */}
      <Icon className={`w-5 h-5 ${iconColor} shrink-0 mt-0.5`} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className={`text-sm font-bold ${titleColor}`}>{toast.title}</h3>
        <p className={`text-xs ${textColor} opacity-80 mt-1`}>
          {toast.message}
        </p>
      </div>

      {/* Close Button */}
      <button
        type="button"
        onClick={onClose}
        className={`
          shrink-0
          p-1
          rounded-md
          transition-colors
          ${
            isSuccess
              ? "hover:bg-emerald-100 text-emerald-600"
              : "hover:bg-red-100 text-red-600"
          }
        `}
        aria-label="Close notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default AdminSettings;
